import React, { useState } from 'react';
import { Calculator, Check, X, Copy, CreditCard } from 'lucide-react';
import { SEO } from '../components/SEO';

interface IbanState {
  iban: string;
  country: 'HR' | 'BA' | 'RS' | 'SI' | 'ME' | 'MK';
  bankCode: string;
  accountNumber: string;
  generatedIban: string;
  isValid: boolean | null;
}

// Country specific IBAN formats and lengths
const countryFormats = {
  'HR': { length: 21, example: 'HR1723600001101234565' },
  'BA': { length: 20, example: 'BA391290079401028494' },
  'RS': { length: 22, example: 'RS35260005601001611379' },
  'SI': { length: 19, example: 'SI56191000000123438' },
  'ME': { length: 22, example: 'ME25505000012345678951' },
  'MK': { length: 19, example: 'MK07250120000058984' }
};

// Bank codes for each country
const bankCodes = {
  'HR': [
    { code: '2340', name: 'Privredna banka Zagreb' },
    { code: '2360', name: 'Zagrebačka banka' },
    { code: '2402', name: 'Erste & Steiermärkische Bank' },
    { code: '2484', name: 'Raiffeisenbank Austria' },
    { code: '2407', name: 'OTP banka' },
    { code: '2330', name: 'Splitska banka' }
  ],
  'BA': [
    { code: '1290', name: 'Raiffeisen Bank' },
    { code: '1610', name: 'UniCredit Bank' },
    { code: '1990', name: 'Sparkasse Bank' },
    { code: '1320', name: 'NLB Banka' },
    { code: '1540', name: 'Intesa Sanpaolo Banka' }
  ],
  'RS': [
    { code: '170', name: 'UniCredit Bank Srbija' },
    { code: '160', name: 'Banca Intesa' },
    { code: '200', name: 'Poštanska štedionica' },
    { code: '265', name: 'Raiffeisen banka' },
    { code: '325', name: 'OTP banka' }
  ],
  'SI': [
    { code: '02', name: 'NLB' },
    { code: '03', name: 'SKB banka' },
    { code: '04', name: 'Nova KBM' },
    { code: '05', name: 'Abanka' },
    { code: '07', name: 'Gorenjska banka' }
  ],
  'ME': [
    { code: '505', name: 'Crnogorska komercijalna banka' },
    { code: '510', name: 'Hipotekarna banka' },
    { code: '520', name: 'Erste Bank' },
    { code: '535', name: 'Prva banka Crne Gore' }
  ],
  'MK': [
    { code: '200', name: 'Stopanska Banka' },
    { code: '210', name: 'NLB Banka' },
    { code: '250', name: 'Sparkasse Bank' },
    { code: '270', name: 'Halk Bank' }
  ]
};

function IbanKalkulator() {
  const [values, setValues] = useState<IbanState>({
    iban: '',
    country: 'HR',
    bankCode: '',
    accountNumber: '',
    generatedIban: '',
    isValid: null
  });

  const [copied, setCopied] = useState<boolean>(false);

  // Function to validate IBAN
  const validateIban = (iban: string): boolean => {
    // Remove spaces and convert to uppercase
    iban = iban.replace(/\s/g, '').toUpperCase();
    
    // Check if the IBAN has the correct length for the country
    const countryCode = iban.substring(0, 2);
    const countryInfo = Object.entries(countryFormats).find(([code]) => code === countryCode);
    
    if (!countryInfo) return false;
    if (iban.length !== countryInfo[1].length) return false;
    
    // Move the first 4 characters to the end
    const rearranged = iban.substring(4) + iban.substring(0, 4);
    
    // Convert letters to numbers (A=10, B=11, ..., Z=35)
    const converted = rearranged.split('').map(char => {
      const code = char.charCodeAt(0);
      return (code >= 65 && code <= 90) ? (code - 55).toString() : char;
    }).join('');
    
    // Perform mod 97 operation
    let remainder = 0;
    for (let i = 0; i < converted.length; i++) {
      remainder = (remainder * 10 + parseInt(converted[i])) % 97;
    }
    
    return remainder === 1;
  };

  // Function to generate IBAN
  const generateIban = (): string => {
    if (!values.country || !values.bankCode || !values.accountNumber) {
      return '';
    }
    
    // Format account number to ensure it has the right length
    let formattedAccount = values.accountNumber.replace(/\s/g, '');
    
    // Calculate the expected length of the account number portion
    const expectedLength = countryFormats[values.country].length;
    const accountLength = expectedLength - 4 - values.bankCode.length; // 4 chars for country code and check digits
    
    // Pad account number if needed
    if (formattedAccount.length < accountLength) {
      formattedAccount = formattedAccount.padStart(accountLength, '0');
    } else if (formattedAccount.length > accountLength) {
      // Trim if too long
      formattedAccount = formattedAccount.substring(formattedAccount.length - accountLength);
    }
    
    // Create IBAN without check digits
    const ibanWithoutCheck = values.country + '00' + values.bankCode + formattedAccount;
    
    // Calculate check digits
    // Move the country code and '00' to the end
    const rearranged = values.bankCode + formattedAccount + values.country + '00';
    
    // Convert letters to numbers (A=10, B=11, ..., Z=35)
    const converted = rearranged.split('').map(char => {
      const code = char.charCodeAt(0);
      return (code >= 65 && code <= 90) ? (code - 55).toString() : char;
    }).join('');
    
    // Calculate check digits using mod 97
    let remainder = 0;
    for (let i = 0; i < converted.length; i++) {
      remainder = (remainder * 10 + parseInt(converted[i])) % 97;
    }
    
    const checkDigits = (98 - remainder).toString().padStart(2, '0');
    
    // Create final IBAN
    return values.country + checkDigits + values.bankCode + formattedAccount;
  };

  const handleValidate = () => {
    const isValid = validateIban(values.iban);
    setValues(prev => ({
      ...prev,
      isValid
    }));
  };

  const handleGenerate = () => {
    const generatedIban = generateIban();
    setValues(prev => ({
      ...prev,
      generatedIban,
      isValid: null
    }));
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatIban = (iban: string): string => {
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = (field: keyof IbanState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      isValid: field === 'iban' ? null : prev.isValid,
      generatedIban: field !== 'iban' ? '' : prev.generatedIban
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "IBAN Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni IBAN kalkulator za provjeru i generiranje IBAN brojeva. Podržava IBAN formate za Hrvatsku, BiH, Srbiju i druge zemlje regije.",
    "url": "https://online-kalkulator.com/iban-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Provjera valjanosti IBAN-a",
      "Generiranje IBAN-a",
      "Podrška za više zemalja",
      "Automatsko formatiranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="IBAN Kalkulator | Provjera i Generiranje IBAN Brojeva"
        description="Besplatni IBAN kalkulator za provjeru i generiranje IBAN brojeva. Podržava IBAN formate za Hrvatsku, BiH, Srbiju i druge zemlje regije."
        canonicalUrl="/iban-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <CreditCard className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">IBAN Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Tabs */}
              <div className="flex border-b">
                <button
                  className={`px-4 py-2 font-medium ${
                    !values.generatedIban ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setValues(prev => ({ ...prev, generatedIban: '' }))}
                >
                  Provjera IBAN-a
                </button>
                <button
                  className={`px-4 py-2 font-medium ${
                    values.generatedIban ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setValues(prev => ({ ...prev, isValid: null }))}
                >
                  Generiranje IBAN-a
                </button>
              </div>

              {/* Validate IBAN Form */}
              {!values.generatedIban && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unesite IBAN za provjeru
                    </label>
                    <input
                      type="text"
                      value={values.iban}
                      onChange={(e) => handleInputChange('iban', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. HR1723600001101234565"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Primjer: {countryFormats[values.country].example}
                    </p>
                  </div>

                  <button
                    onClick={handleValidate}
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Provjeri IBAN
                  </button>

                  {values.isValid !== null && (
                    <div className={`p-4 rounded-lg ${values.isValid ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex items-center">
                        {values.isValid ? (
                          <Check className="w-6 h-6 text-green-500 mr-2" />
                        ) : (
                          <X className="w-6 h-6 text-red-500 mr-2" />
                        )}
                        <p className={`text-lg font-medium ${values.isValid ? 'text-green-700' : 'text-red-700'}`}>
                          {values.isValid ? 'IBAN je valjan' : 'IBAN nije valjan'}
                        </p>
                      </div>
                      {!values.isValid && (
                        <p className="mt-2 text-sm text-red-600">
                          Provjerite jeste li ispravno unijeli IBAN broj.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Generate IBAN Form */}
              {values.generatedIban === '' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Država
                      </label>
                      <select
                        value={values.country}
                        onChange={(e) => handleInputChange('country', e.target.value as IbanState['country'])}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="HR">Hrvatska</option>
                        <option value="BA">Bosna i Hercegovina</option>
                        <option value="RS">Srbija</option>
                        <option value="SI">Slovenija</option>
                        <option value="ME">Crna Gora</option>
                        <option value="MK">Sjeverna Makedonija</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Banka
                      </label>
                      <select
                        value={values.bankCode}
                        onChange={(e) => handleInputChange('bankCode', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Odaberite banku</option>
                        {bankCodes[values.country].map(bank => (
                          <option key={bank.code} value={bank.code}>
                            {bank.name} ({bank.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Broj računa
                    </label>
                    <input
                      type="text"
                      value={values.accountNumber}
                      onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite broj računa"
                    />
                  </div>

                  <button
                    onClick={handleGenerate}
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    disabled={!values.bankCode || !values.accountNumber}
                  >
                    Generiraj IBAN
                  </button>
                </div>
              )}

              {/* Generated IBAN Result */}
              {values.generatedIban && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Generirani IBAN:</p>
                        <p className="text-xl font-mono font-bold text-blue-700">
                          {formatIban(values.generatedIban)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCopy(values.generatedIban)}
                        className="p-2 text-gray-500 hover:text-blue-600 transition"
                        title="Kopiraj IBAN"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setValues(prev => ({
                      ...prev,
                      generatedIban: '',
                      bankCode: '',
                      accountNumber: ''
                    }))}
                    className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Generiraj novi IBAN
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što je IBAN?</h2>
                <p className="text-gray-600 leading-relaxed">
                  IBAN (International Bank Account Number) je međunarodni standard za numeraciju bankovnih računa. 
                  Osmišljen je kako bi olakšao prekogranične transakcije i smanjio pogreške pri unosu podataka. 
                  IBAN sadrži do 34 alfanumerička znaka koji uključuju oznaku države, kontrolne znamenke, 
                  identifikacijski broj banke i broj računa.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Struktura IBAN-a</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opća struktura</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Oznaka države (2 slova)</li>
                      <li>Kontrolne znamenke (2 broja)</li>
                      <li>Identifikacijski broj banke (SWIFT/BIC kod)</li>
                      <li>Broj računa</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Primjer za Hrvatsku</h3>
                    <p className="text-gray-600 mb-2">
                      IBAN: <span className="font-mono">HR17 2360 0001 1012 3456 5</span>
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li><span className="font-mono">HR</span> - oznaka države (Hrvatska)</li>
                      <li><span className="font-mono">17</span> - kontrolne znamenke</li>
                      <li><span className="font-mono">2360</span> - identifikacijski broj banke (Zagrebačka banka)</li>
                      <li><span className="font-mono">0001101234565</span> - broj računa</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">IBAN Format po Državama</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Država
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duljina
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Format
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Hrvatska (HR)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">21</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">HR00 0000 0000 0000 0000 0</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Bosna i Hercegovina (BA)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">20</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">BA00 0000 0000 0000 0000</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Srbija (RS)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">22</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">RS00 000 0000 0000 0000 00</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Slovenija (SI)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">19</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">SI00 0000 0000 0000 000</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Crna Gora (ME)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">22</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">ME00 000 0000 0000 0000 00</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Sjeverna Makedonija (MK)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">19</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">MK00 000 0000 0000 000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti IBAN Kalkulator</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Za provjeru IBAN-a:</h3>
                    <ol className="list-decimal list-inside text-gray-600 space-y-1">
                      <li>Unesite IBAN koji želite provjeriti</li>
                      <li>Kliknite na gumb "Provjeri IBAN"</li>
                      <li>Kalkulator će provjeriti je li IBAN valjan</li>
                    </ol>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Za generiranje IBAN-a:</h3>
                    <ol className="list-decimal list-inside text-gray-600 space-y-1">
                      <li>Odaberite državu</li>
                      <li>Odaberite banku</li>
                      <li>Unesite broj računa</li>
                      <li>Kliknite na gumb "Generiraj IBAN"</li>
                      <li>Kopirajte generirani IBAN</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>IBAN se obično piše u grupama od po četiri znaka radi lakšeg čitanja</li>
                  <li>Prilikom elektroničkog unosa, razmaci se ignoriraju</li>
                  <li>IBAN može sadržavati samo slova i brojeve (bez posebnih znakova)</li>
                  <li>Kontrolne znamenke služe za provjeru ispravnosti IBAN-a</li>
                  <li>Različite države imaju različite duljine i formate IBAN-a</li>
                  <li>Generirani IBAN je informativne prirode i treba ga provjeriti s bankom</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Česta Pitanja</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Što ako moj IBAN nije valjan?</h3>
                    <p className="text-gray-600">
                      Provjerite jeste li ispravno unijeli sve znamenke. Česte pogreške uključuju zamjenu slova i brojeva, 
                      izostavljanje znakova ili dodavanje razmaka na pogrešnim mjestima.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Gdje mogu pronaći svoj IBAN?</h3>
                    <p className="text-gray-600">
                      IBAN možete pronaći na izvodu iz banke, u internet ili mobilnom bankarstvu, 
                      ili ga možete zatražiti direktno od svoje banke.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Je li IBAN isto što i broj računa?</h3>
                    <p className="text-gray-600">
                      Ne, IBAN je međunarodni format koji uključuje broj računa, ali sadrži i dodatne informacije 
                      poput oznake države, kontrolnih znamenki i identifikacijskog broja banke.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IbanKalkulator;